import React, { useState, useEffect } from 'react';
import { auditLogAPI } from '../services/api';
import { useToast } from './Toast';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 20;

const AuditLog = () => {
  const { showToast } = useToast();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');

  useEffect(() => {
    loadLogs();
  }, [page, entityType]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (entityType) params.entity_type = entityType;
      const response = await auditLogAPI.getAll(params);
      const data = response.data;
      setLogs(data.logs || []);
      setTotal(data.total ?? 0);
      setTotalPages(data.totalPages ?? 1);
    } catch (err) {
      console.error('Failed to load audit log:', err);
      showToast(err.response?.data?.error || 'Failed to load audit log', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-text-primary">Audit Log</h1>
        <select
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-neutral-border rounded-button text-text-primary text-sm max-w-[180px]"
        >
          <option value="">All types</option>
          <option value="lead">Leads</option>
        </select>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-500 border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-neutral-bg border-b border-neutral-border">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">Time</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">User</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">Action</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">Entity</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-border">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-neutral-bg/50">
                    <td className="py-3 px-4 text-sm text-text-secondary">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-text-primary">{log.username || log.user_id || '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        log.action === 'CREATE' ? 'bg-green-100 text-green-800' :
                        log.action === 'UPDATE' ? 'bg-blue-100 text-blue-800' :
                        log.action === 'DELETE' ? 'bg-red-100 text-red-800' : 'bg-neutral-100 text-neutral-700'
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-text-primary">{log.entity_type || '-'}</td>
                    <td className="py-3 px-4 text-sm text-text-secondary">{log.entity_id || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-border">
            <p className="text-sm text-text-secondary">
              Page {page} of {totalPages} ({total} total)
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
                disabled={page <= 1 || loading}
                className="p-2 rounded border border-neutral-border disabled:opacity-50"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); }}
                disabled={page >= totalPages || loading}
                className="p-2 rounded border border-neutral-border disabled:opacity-50"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
